// Side cart drawer shared by every header that uses the "drawer" cart layout
// (grocery, meat, fish, pet, restaurant). Owns the drawer markup, the
// show-all-lines toggle and the edit-line modal; the header only controls
// open/close and decides what "checkout" means (navigate vs. ask to log in).
//
// `classPrefix` keeps each skin's existing class names (`${prefix}-cart-drawer`,
// `${prefix}-drawer-line`, …) so per-industry SCSS keeps applying unchanged.
import { useEffect, useState } from 'react';
import { Drawer, Grid } from 'antd';
import { DeleteOutlined, EditOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import {
  useActiveCart,
  useClearCart,
  useRemoveCartLine,
  useUpdateCartLine,
} from '@core/hooks/useCart';
import { useCartLineNote } from '@core/hooks/useCartLineNote';
import { getChargeRows } from '@core/utils/charges';
import { useStoreClosed } from '@core/utils/storeStatus';
import { useThemePlaceholderSrc } from '@core/utils/themePlaceholder';
import Button from '@components/base/Button';
import CartLineDetails from '@components/base/CartLineDetails';
import CartLineNote from '@components/base/CartLineNote';
import ImageWithFallback from '@components/base/ImageWithFallback';
import Price from '@components/base/Price';
import ProductDetailView from '@features/product-detail/ProductDetailView.jsx';
import AppModal from '@components/base/AppModal';
import { useRetainedWhileClosing } from '@components/base/AppModal/useRetainedWhileClosing';
import { useCopy } from '@/app/providers/useCopy';

const COLLAPSED_LINE_COUNT = 4;

export default function CartDrawer({
  open,
  onClose,
  onCheckout,
  classPrefix = 'grocery',
  editModalClassName = `${classPrefix}-product-modal`,
}) {
  const t = useCopy();
  const { cart, lineCount } = useActiveCart();
  const clearCart = useClearCart();
  const updateCartLine = useUpdateCartLine();
  const removeCartLine = useRemoveCartLine();
  const { checkStoreOpenOrShowModal } = useStoreClosed();
  const { saveNote, isSavingNote } = useCartLineNote();
  const themePlaceholderSrc = useThemePlaceholderSrc();
  const screens = Grid.useBreakpoint();
  // Below 36rem the mobile cart bar / cart page replaces the drawer.
  const isDrawerViewport = Boolean(screens.sm);
  const [showAllLines, setShowAllLines] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [shownLine, releaseLine] = useRetainedWhileClosing(editingLine);

  useEffect(() => {
    if (!isDrawerViewport && open) onClose?.();
  }, [isDrawerViewport, open, onClose]);

  useEffect(() => {
    if (!open || !isDrawerViewport) setShowAllLines(false);
  }, [open, isDrawerViewport]);

  const p = classPrefix;
  const cartItems = cart?.items ?? [];
  const isCheckoutDisabled = cartItems.length === 0 || clearCart.isPending;
  const totalItemQuantity = cartItems.reduce((sum, line) => sum + (line.quantity ?? 0), 0);
  const chargeRows = getChargeRows(cart);
  // Keep the line's note on quantity changes so a PATCH never drops it.
  const changeQuantity = (line, quantity) =>
    updateCartLine.mutate({ lineId: line.lineId, quantity, notes: line.notes });
  const shouldCollapseLines = !isDrawerViewport;
  const visibleItems =
    shouldCollapseLines && !showAllLines ? cartItems.slice(0, COLLAPSED_LINE_COUNT) : cartItems;
  const hasHiddenLines = shouldCollapseLines && cartItems.length > COLLAPSED_LINE_COUNT;

  const handleCheckout = () => {
    if (isCheckoutDisabled) return;
    if (!checkStoreOpenOrShowModal()) return;
    onClose?.();
    onCheckout?.();
  };

  return (
    <>
      <Drawer
        className={`${p}-cart-drawer`}
        title={
          <span className={`${p}-drawer-title`}>
            <ShoppingCartOutlined />
            <strong>{t('yourCart', 'Your Cart')}</strong>
            <small>
              ({lineCount} {t('items', 'items')})
            </small>
          </span>
        }
        extra={
          lineCount > 0 ? (
            <Button
              className={`${p}-drawer-clear-all-button`}
              type="text"
              size="small"
              onClick={() => clearCart.mutate()}
              loading={clearCart.isPending}
            >
              {t('clearAll', 'Clear all')}
            </Button>
          ) : null
        }
        open={open}
        onClose={onClose}
        width={isDrawerViewport ? 500 : '100%'}
        placement="right"
        maskClosable
        keyboard
      >
        <div className={`${p}-drawer-layout`}>
          <div className={`${p}-drawer-lines`}>
            {visibleItems.map((line) => (
              <article className={`${p}-drawer-line cart-drawer-line`} key={line.lineId}>
                <ImageWithFallback
                  src={line.imageUrl}
                  alt={line.itemName}
                  aspectRatio="1 / 1"
                  radius={10}
                  fallbackSrc={themePlaceholderSrc}
                />
                <div className={`${p}-drawer-line-main`}>
                  <CartLineDetails line={line} t={t} />
                </div>
                <div className={`${p}-drawer-line-actions`}>
                  {!line.isUpsellingItem && (
                    <button
                      type="button"
                      aria-label={t('edit', 'Edit') + ` ${line.itemName}`}
                      onClick={() => setEditingLine(line)}
                    >
                      <EditOutlined />
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label={t('remove', 'Remove') + ` ${line.itemName}`}
                    onClick={() => removeCartLine.mutate(line.lineId)}
                  >
                    <DeleteOutlined />
                  </button>
                </div>
                <div className={`${p}-drawer-stepper`}>
                  <button
                    type="button"
                    disabled={line.quantity <= 1}
                    onClick={() => changeQuantity(line, line.quantity - 1)}
                  >
                    −
                  </button>
                  <span>{line.quantity}</span>
                  <button type="button" onClick={() => changeQuantity(line, line.quantity + 1)}>
                    +
                  </button>
                </div>
                <CartLineNote
                  value={line.notes}
                  onSave={(notes) => saveNote(line, notes)}
                  isSaving={isSavingNote(line)}
                  t={t}
                />
              </article>
            ))}
            {hasHiddenLines ? (
              <div className={`${p}-drawer-lines-toggle`}>
                <Button
                  type="default"
                  block
                  onClick={() => setShowAllLines((value) => !value)}
                  aria-expanded={showAllLines}
                >
                  {showAllLines
                    ? t('showFewerItems', 'Show fewer items')
                    : t('showAllItems', `Show all items (${cartItems.length})`)}
                </Button>
              </div>
            ) : null}
          </div>

          <div className={`${p}-drawer-bottom`}>
            <div className={`${p}-drawer-total`}>
              <div>
                <span>{t('totalItems', 'Total items')}</span>
                <strong>{cartItems.length}</strong>
              </div>
              <div>
                <span>{t('totalItemsQuantity', 'Total items quantity')}</span>
                <strong>{totalItemQuantity}</strong>
              </div>
              <div>
                <span>{t('subtotal', 'Subtotal')}</span>
                <Price amount={cart?.subtotal ?? 0} />
              </div>
              <div>
                <span>{t('tax', 'Tax')}</span>
                <Price amount={cart?.taxTotal ?? 0} />
              </div>
              {chargeRows.map((charge) => (
                <div key={charge.id}>
                  <span>{charge.label}</span>
                  <Price amount={charge.amount} />
                </div>
              ))}
              <div className={`${p}-drawer-divider`} />
              <div className={`${p}-drawer-grand-total`}>
                <strong>{t('total', 'Total')}</strong>
                <Price amount={cart?.grandTotal ?? 0} size={22} />
              </div>
              <Button
                type="primary"
                size="large"
                block
                disabled={isCheckoutDisabled}
                onClick={handleCheckout}
              >
                {t('proceedToCheckout', 'Proceed to Checkout')}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>

      <AppModal
        kind="product"
        open={Boolean(editingLine)}
        footer={null}
        onCancel={() => setEditingLine(null)}
        afterClose={releaseLine}
        width={isDrawerViewport ? 780 : 'calc(100% - 2rem)'}
        centered={!isDrawerViewport}
        destroyOnHidden
        zIndex={4100}
        className={`${editModalClassName} product-detail-modal`}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            overflow: 'hidden',
            borderRadius: 6,
            background: '#ffffff',
            maxHeight: isDrawerViewport ? '80vh' : '100vh',
          },
        }}
      >
        {shownLine ? (
          <ProductDetailView
            productId={shownLine.itemId}
            inModal
            cartLine={shownLine}
            onDone={() => setEditingLine(null)}
          />
        ) : null}
      </AppModal>
    </>
  );
}
